/**
 * Utility functions for smooth scrolling in Ionic components
 */

/**
 * Scrolls to a specific target element within an IonContent component
 */
export const scrollToTarget = async (
  contentRef: React.RefObject<HTMLIonContentElement>,
  targetRef: React.RefObject<HTMLDivElement | null>
): Promise<void> => {
  if (contentRef.current && targetRef.current) {
    try {
      // Get the element's position relative to the content
      const contentElement = contentRef.current;
      const targetElement = targetRef.current;
      
      // Use getBoundingClientRect to get accurate positioning
      const contentRect = await contentElement.getScrollElement().then(scrollEl => scrollEl.getBoundingClientRect());
      const targetRect = targetElement.getBoundingClientRect();
      
      // Calculate the scroll position relative to the content's current scroll position
      const currentScrollTop = await contentElement.getScrollElement().then(scrollEl => scrollEl.scrollTop);
      const targetY = currentScrollTop + (targetRect.top - contentRect.top);
      
      await contentElement.scrollToPoint(0, targetY, 500);
    } catch (error) {
      // Fallback: use scrollIntoView which should work better in most cases
      targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
};

/**
 * Scrolls to the top of an IonContent component
 */
export const scrollToTop = (contentRef: React.RefObject<HTMLIonContentElement | null>): void => {
  if (contentRef.current) {
    try {
      contentRef.current.scrollToTop(500);
    } catch (error) {
      console.warn('Failed to scroll to top:', error);
    }
  }
};

/**
 * Scrolls to the bottom of an IonContent component
 */
export const scrollToBottom = (contentRef: React.RefObject<HTMLIonContentElement | null>): void => {
  if (contentRef.current) {
    try {
      contentRef.current.scrollToBottom(500);
    } catch (error) {
      console.warn('Failed to scroll to bottom:', error);
    }
  }
};

/**
 * Determines if auto-scrolling should be enabled based on content height
 */
export const shouldAutoScroll = (contentRef: React.RefObject<HTMLIonContentElement | null>): boolean => {
  if (!contentRef.current) return false;
  
  try {
    // Check if content height exceeds viewport height
    const contentElement = contentRef.current;
    const scrollHeight = contentElement.scrollHeight;
    const clientHeight = contentElement.clientHeight;
    
    return scrollHeight > clientHeight;
  } catch (error) {
    // If we can't determine dimensions, err on the side of caution and don't scroll
    return false;
  }
};
